import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch
} from 'react-router-dom';
import { ApplicationState } from '../../../../store';
import { saveUser } from '../../../../store/ducks/user/actions';
import {
  ECodeType,
  ESpreadsheetField,
  EUserPreferenceType,
  IOrderEntryPreference,
  ISaveUser
} from '../../../../store/ducks/user/types';
import RepeatOrder from './RepeatOrder';
import SearchItem from './SearchItem';
import SpreadsheetUploadMvp from './SpreadsheetUploadMvp';
import Catalog from '../Catalog';
import { EPurchaseRoutes } from '../../purchase.routes';
import { ProductInfoPage } from '../ProductInfo';

import CheckoutMVP from '../CheckoutMVP';
import OrderConfirmedMvp from '../CheckoutMVP/OrderConfirmed';
import { GetFlag } from '../../../../utils/featureFlag';
import { SelfSpreadsheetUploadMvp } from './SpreadsheetUploadMvp/SelfSpreadsheetUploadMvp';
import { ShoppingCartWrapper } from '../ShoppingCartMVP/ShoppingCartWrapper';
import { ShoppingCartWrapperAutoSync } from '../ShoppingCartMVP/ShoppingCartWrapperAutoSync';
import { setShowCommerceTokenModal } from '../../../../store/ducks/auth/actions';

const orderEntryPreferenceDefault: IOrderEntryPreference = {
  name: EUserPreferenceType.ORDER_ENTRY,
  codeType: ECodeType.SKU,
  spreadsheetLine: 3,
  spreadsheetColumns: [
    {
      key: 0,
      field: ESpreadsheetField.SKU,
      label: 'Código da Gerdau',
      position: 'A'
    },
    {
      key: 1,
      field: ESpreadsheetField.CLIENT_SKU,
      label: 'Meu código interno',
      position: ''
    },
    {
      key: 2,
      field: ESpreadsheetField.QUANTITY,
      label: 'Quantidade',
      position: 'B'
    },
    { key: 3, field: ESpreadsheetField.UNITY, label: 'Unidade', position: 'C' },
    {
      key: 4,
      field: ESpreadsheetField.DESIRED_DATE,
      label: 'Data desejada',
      position: 'D'
    },
    {
      key: 5,
      field: ESpreadsheetField.CUSTOMER_PURCHASE_ITEM,
      label: 'Item do pedido',
      position: 'E'
    }
  ]
};

export enum EOrderSettingsRoutes {
  CHOOSE = 'steel-type-choose',
  LONG_STEEL = 'long-steel',
  SEARCH_ITEMS = 'search-items',
  SPREADSHEET_UPLOAD = 'spreadsheet-upload',
  SHOPPING_CART = 'shopping-cart',
  REPEAT_ORDER = 'repeat-order',
  CHECKOUT = 'checkout',
  CATALOG = 'catalog',
  PRODUCT = 'product',
  FINALIZE_ORDER = 'finalize-order',
  LAST_ORDERS = 'last-orders'
}

export const longSteelRoutes = [
  EOrderSettingsRoutes.CATALOG,
  EOrderSettingsRoutes.SEARCH_ITEMS,
  EOrderSettingsRoutes.SPREADSHEET_UPLOAD,
  EOrderSettingsRoutes.REPEAT_ORDER,
  EOrderSettingsRoutes.SHOPPING_CART,
  EOrderSettingsRoutes.CHECKOUT,
  EOrderSettingsRoutes.FINALIZE_ORDER
];

interface OrderSettingsRoutesProps {
  crossSellKey?: string;
  setCrossSellKey?: React.Dispatch<React.SetStateAction<string>>;
  autoSyncLoading?: boolean;
  setAutoSyncLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrderSettingsRoutes: React.FC<OrderSettingsRoutesProps> = ({
  crossSellKey,
  setCrossSellKey,
  autoSyncLoading,
  setAutoSyncLoading
}) => {
  const { path } = useRouteMatch();
  const { pathname } = useLocation();

  const { emitterId, receiverId } = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const dispatch = useDispatch();
  const selfUploadSheetFlag = GetFlag('self_upload_sheet');
  const cartUpdateDebounceSync = GetFlag('cart_update_debounce_sync');

  const getUserOrderEntryPreference = (): IOrderEntryPreference => {
    const orderEntryIdx = user.preferences.findIndex(
      (preference) => preference.name === EUserPreferenceType.ORDER_ENTRY
    );

    if (
      orderEntryIdx !== -1 &&
      (user.preferences[orderEntryIdx] as IOrderEntryPreference)
        .spreadsheetColumns
    ) {
      const orderEntryPreferences = user.preferences[
        orderEntryIdx
      ] as IOrderEntryPreference;

      if (
        orderEntryPreferences.spreadsheetColumns.length <
        orderEntryPreferenceDefault.spreadsheetColumns.length
      ) {
        orderEntryPreferenceDefault.spreadsheetColumns.forEach((col) => {
          if (
            !orderEntryPreferences.spreadsheetColumns.find(
              (item) => item.field === col.field
            )
          ) {
            orderEntryPreferences.spreadsheetColumns.push({
              ...col,
              position: ''
            });
          }
        });
      }

      return orderEntryPreferences;
    } else {
      return orderEntryPreferenceDefault;
    }
  };

  const user = useSelector((state: ApplicationState) => state.user);
  const [userOrderEntryPreference, setUserOrderEntryPreference] =
    useState<IOrderEntryPreference>(getUserOrderEntryPreference());

  const updateUserOrderEntryPreference = (
    newUserOrderEntryPreference: IOrderEntryPreference
  ) => {
    const saveUserProps: ISaveUser = {
      user: user,
      userPreference: newUserOrderEntryPreference
    };
    setUserOrderEntryPreference(newUserOrderEntryPreference);
    dispatch(saveUser(saveUserProps));
  };

  const commerceTokenAvailable = useSelector(
    (appState: ApplicationState) => appState.auth.commerceTokenAvailable
  );

  useEffect(() => {
    if (commerceTokenAvailable) return;
    if (longSteelRoutes.some((route) => pathname.includes(route))) {
      dispatch(setShowCommerceTokenModal(true));
      return;
    }
  }, [pathname, commerceTokenAvailable]);

  const isLongSteelRoot = path.endsWith(EPurchaseRoutes.LONG_STEEL);

  return (
    <Switch>
      {isLongSteelRoot && (
        <Route path={`${path}/`} exact>
          <Redirect to={`${path}/${EOrderSettingsRoutes.CHOOSE}`} />
        </Route>
      )}

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.REPEAT_ORDER}`}
        exact
      >
        <RepeatOrder></RepeatOrder>
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.SEARCH_ITEMS}`}
        exact
      >
        <SearchItem
          emitterId={emitterId}
          userOrderEntryPreference={userOrderEntryPreference}
          updateUserOrderEntryPreference={updateUserOrderEntryPreference}
        />
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.CATALOG}`}
        exact
      >
        <Catalog key={emitterId} />
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.SPREADSHEET_UPLOAD}`}
        exact
      >
        {selfUploadSheetFlag ? (
          <SelfSpreadsheetUploadMvp
            emitterId={emitterId}
            receiverId={receiverId}
          />
        ) : (
          <SpreadsheetUploadMvp emitterId={emitterId} />
        )}
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.SHOPPING_CART}`}
        exact
      >
        {cartUpdateDebounceSync ? (
          <ShoppingCartWrapperAutoSync
            crossSellKey={crossSellKey}
            setCrossSellKey={setCrossSellKey}
            autoSyncLoading={autoSyncLoading}
            setAutoSyncLoading={setAutoSyncLoading}
          />
        ) : (
          <ShoppingCartWrapper
            crossSellKey={crossSellKey}
            setCrossSellKey={setCrossSellKey}
          />
        )}
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.CHECKOUT}`}
        exact
      >
        <CheckoutMVP />
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.FINALIZE_ORDER}`}
        exact
      >
        <OrderConfirmedMvp emitterId={emitterId} receiverId={receiverId} />
      </Route>

      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.CATALOG}/${EOrderSettingsRoutes.PRODUCT}/:sku`}
      >
        <ProductInfoPage emitterId={emitterId} receiverId={receiverId} />
      </Route>
    </Switch>
  );
};

export default OrderSettingsRoutes;
